import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import { clsx } from 'clsx';
import { type Control, Controller, type Path } from 'react-hook-form';
import { IconButton } from '@repo/ui/IconButton';
import { Flex } from '@repo/ui/Flex';
import type { WorkflowData } from '../../workflow';
import s from './QuantitySelector.module.css';

// Type helper to construct a valid Path for quantity fields
// Uses a const assertion to help TypeScript understand the structure
function createQuantityFieldPath(itemId: string): Path<WorkflowData> {
  const path = `items.${itemId}.itemSelection.quantity` as const;
  // TypeScript can't validate dynamic template strings at compile time,
  // but this path structure is guaranteed to be valid for WorkflowData
  // The assertion is necessary because Path<WorkflowData> is a union of
  // specific string literals, and we're constructing one dynamically
  return path as Path<WorkflowData>;
}

export interface QuantitySelectorProps {
  itemId?: string;
  /** Called when quantity changes. Receives the new value as a string. */
  onQuantityChange?: (value: string) => void;
  remainingQuantity: number;
  disabled?: boolean;
  control?: Control<WorkflowData>;
  value?: number;
  min?: number;
  className?: string;
  'data-testid'?: string;
}

const handleDecrement = (
  currentValue: number,
  onChange: (value: number) => void,
  onQuantityChange?: (value: string) => void,
  min = 1,
) => {
  if (currentValue > min) {
    const newValue = currentValue - 1;
    onChange(newValue);
    onQuantityChange?.(newValue.toString());
  }
};

const handleIncrement = (
  currentValue: number,
  onChange: (value: number) => void,
  remainingQuantity: number,
  onQuantityChange?: (value: string) => void,
) => {
  if (currentValue < remainingQuantity) {
    const newValue = currentValue + 1;
    onChange(newValue);
    onQuantityChange?.(newValue.toString());
  }
};

/** Clamps quantity value between min and remainingQuantity */
const clampQuantity = (
  numValue: number,
  remainingQuantity: number,
  min = 1,
): number => {
  if (isNaN(numValue) || numValue < min) {
    return min;
  }
  if (numValue > remainingQuantity) {
    return remainingQuantity;
  }
  return numValue;
};

const UncontrolledQuantitySelector = ({
  value,
  onQuantityChange,
  remainingQuantity,
  disabled,
  className,
  dataTestId,
  min = 1,
}: {
  value?: number;
  onQuantityChange?: (value: string) => void;
  remainingQuantity: number;
  disabled: boolean;
  className?: string;
  dataTestId: string;
  min: number;
}) => {
  const initialValue = value ?? min;
  const [inputValue, setInputValue] = useState(initialValue.toString());
  const isFocusedRef = useRef(false);

  // Get current numeric value from input (clamped to valid range)
  // This ensures stepper operations use what's actually displayed, not stale prop value
  const getCurrentValue = (): number => {
    const numValue = parseInt(inputValue, 10);
    return clampQuantity(numValue, remainingQuantity, min);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onQuantityChange?.(e.target.value);
  };

  const handleInputFocus = () => {
    isFocusedRef.current = true;
  };

  const handleInputBlur = () => {
    isFocusedRef.current = false;
    const clampedValue = getCurrentValue();
    setInputValue(clampedValue.toString());
    onQuantityChange?.(clampedValue.toString());
  };

  // Sync input value when external value changes (but not while user is typing)
  useEffect(() => {
    if (
      !isFocusedRef.current &&
      value !== undefined &&
      value.toString() !== inputValue
    ) {
      setInputValue(value.toString());
    }
  }, [value, inputValue]);

  const currentValue = getCurrentValue();

  return (
    <Flex
      className={clsx(s.quantitySelector, className)}
      data-testid={dataTestId}
      alignItems="stretch"
    >
      <IconButton
        icon="minus"
        label="Decrease quantity"
        size="sm"
        variant="secondary"
        disabled={disabled || currentValue <= min}
        onClick={() => {
          handleDecrement(
            currentValue,
            (newValue) => setInputValue(newValue.toString()),
            onQuantityChange,
            min,
          );
        }}
      />
      <input
        type="number"
        className={s.quantityValue}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        disabled={disabled}
        min={min}
        max={remainingQuantity}
        aria-label="Quantity"
        data-testid={`${dataTestId}-input`}
        role="spinbutton"
      />
      <IconButton
        icon="plus"
        label="Increase quantity"
        size="sm"
        variant="secondary"
        disabled={disabled || currentValue >= remainingQuantity}
        onClick={() => {
          handleIncrement(
            currentValue,
            (newValue) => setInputValue(newValue.toString()),
            remainingQuantity,
            onQuantityChange,
          );
        }}
      />
    </Flex>
  );
};

const ControlledQuantitySelector = ({
  fieldName,
  control,
  onQuantityChange,
  remainingQuantity,
  disabled,
  className,
  dataTestId,
  min = 1,
}: {
  fieldName: Path<WorkflowData>;
  control: Control<WorkflowData>;
  onQuantityChange?: (value: string) => void;
  remainingQuantity: number;
  disabled: boolean;
  className?: string;
  dataTestId: string;
  min: number;
}) => {
  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field }) => {
        const initialValue =
          typeof field.value === 'number' ? field.value : min;
        const [inputValue, setInputValue] = useState(initialValue.toString());
        const isFocusedRef = useRef(false);

        // Get current numeric value from input (clamped to valid range)
        // This ensures stepper operations use what's actually displayed, not stale field.value
        const getCurrentValue = (): number => {
          const numValue = parseInt(inputValue, 10);
          return clampQuantity(numValue, remainingQuantity, min);
        };

        const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
          setInputValue(e.target.value);
          onQuantityChange?.(e.target.value);
        };

        const handleInputFocus = () => {
          isFocusedRef.current = true;
        };

        const handleInputBlur = () => {
          isFocusedRef.current = false;
          const clampedValue = getCurrentValue();
          setInputValue(clampedValue.toString());
          field.onChange(clampedValue);
          onQuantityChange?.(clampedValue.toString());
          field.onBlur();
        };

        // Sync input value when field value changes (but not while user is typing)
        useEffect(() => {
          if (
            !isFocusedRef.current &&
            field.value !== undefined &&
            field.value.toString() !== inputValue
          ) {
            setInputValue(field.value.toString());
          }
        }, [field.value, inputValue]);

        const currentValue = getCurrentValue();

        return (
          <Flex
            className={clsx(s.quantitySelector, className)}
            data-testid={dataTestId}
            alignItems="stretch"
          >
            <IconButton
              icon="minus"
              label="Decrease quantity"
              size="sm"
              variant="secondary"
              disabled={disabled || currentValue <= min}
              onClick={() => {
                handleDecrement(
                  currentValue,
                  (newValue) => {
                    setInputValue(newValue.toString());
                    field.onChange(newValue);
                  },
                  onQuantityChange,
                  min,
                );
              }}
            />
            <input
              type="number"
              className={s.quantityValue}
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              disabled={disabled}
              min={min}
              max={remainingQuantity}
              aria-label="Quantity"
              data-testid={`${dataTestId}-input`}
              role="spinbutton"
            />
            <IconButton
              icon="plus"
              label="Increase quantity"
              size="sm"
              variant="secondary"
              disabled={disabled || currentValue >= remainingQuantity}
              onClick={() => {
                handleIncrement(
                  currentValue,
                  (newValue) => {
                    setInputValue(newValue.toString());
                    field.onChange(newValue);
                  },
                  remainingQuantity,
                  onQuantityChange,
                );
              }}
            />
          </Flex>
        );
      }}
    />
  );
};

export const QuantitySelector = ({
  itemId,
  onQuantityChange,
  remainingQuantity,
  disabled = false,
  control,
  value,
  min = 1,
  className,
  'data-testid': dataTestId = 'quantitySelector',
}: QuantitySelectorProps) => {
  const fieldName = itemId ? createQuantityFieldPath(itemId) : undefined;

  if (!control || !itemId || !fieldName) {
    return (
      <UncontrolledQuantitySelector
        value={value}
        onQuantityChange={onQuantityChange}
        remainingQuantity={remainingQuantity}
        disabled={disabled}
        className={className}
        dataTestId={dataTestId}
        min={min}
      />
    );
  }

  return (
    <ControlledQuantitySelector
      fieldName={fieldName}
      control={control}
      onQuantityChange={onQuantityChange}
      remainingQuantity={remainingQuantity}
      disabled={disabled}
      className={className}
      dataTestId={dataTestId}
      min={min}
    />
  );
};
